// Renders one Store Action in compact or table form and dispatches its resolved interaction.
import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RACDateTimeLabel,
  Typography,
} from '@rentacenter/racstrap';
import { faCheckCircle, faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

import { FeatureFlagContext } from '../../../context/featureFlagContext';
import { CompactStoreActionCategory, StoreAction } from './storeActionDomain';
import {
  getStoreActionMessage,
  localizeStoreActionDescription,
} from './storeActionLocalization';
import { PRINT_EDIT_PRICETAG_DESTINATION } from './storeActionPriceTag';
import { getStoreActionRouteOutcome } from './storeActionRouting';
import {
  getStoreActionThemeFallbackColor,
  StoreActionThemeToken,
} from './storeActionTheme';
import { resolveStoreActionCategory } from './storeActionTypeCatalog';
import { useStoreActionStatusMutation } from './storeActionMutations';
import { hasInventoryId } from './StoreActionTransferDialog';
import { canManageCompletedStoreActions } from './storeActionPermissions';

type StoreActionFeatureFlags = {
  readonly dateTimeLocalization?: unknown;
  readonly countryIdentification?: unknown;
};

type StoreActionRowProps = {
  readonly className?: string;
  readonly item: StoreAction;
  readonly presentation?: 'compact' | 'table';
  readonly role?: string;
  readonly storeNumber?: string;
  readonly onDssActivate?: (item: StoreAction, storeNumber: string) => void;
  readonly onTransferActivate?: (
    item: StoreAction,
    type: 'EAITF' | 'EAITT',
    trigger: HTMLElement
  ) => void;
};

const isDateTimeLocalizationEnabled = (value: unknown): boolean =>
  typeof value === 'number' || typeof value === 'string'
    ? Number(value) === 1
    : false;

const getCountryIdentification = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const CATEGORY_LABEL_KEYS: Readonly<
  Record<
    Exclude<CompactStoreActionCategory, 'ALL'>,
    Parameters<typeof getStoreActionMessage>[0]
  >
> = {
  AGR: 'categoryAgreement',
  INV: 'categoryInventory',
  CUS: 'categoryCustomer',
  FACTPAY: 'categoryInvoice',
  OTHER: 'categoryOther',
};

type StoreActionStatusControlProps = {
  readonly item: StoreAction;
  readonly presentation: 'compact' | 'table';
  readonly storeNumber: string;
};

type StoreActionRowActivation = {
  readonly isNavigable: boolean;
  readonly isUnavailable: boolean;
  readonly activate: (trigger?: HTMLElement) => void;
};

const getStoreActionRowActivation = (
  item: StoreAction,
  push: ReturnType<typeof useHistory>['push'],
  storeNumber?: string,
  onDssActivate?: (item: StoreAction, storeNumber: string) => void,
  onTransferActivate?: (
    item: StoreAction,
    type: 'EAITF' | 'EAITT',
    trigger: HTMLElement
  ) => void
): StoreActionRowActivation => {
  const routeOutcome = getStoreActionRouteOutcome(item);
  const isTransfer =
    routeOutcome.kind === 'special' &&
    (routeOutcome.action === 'EAITF' || routeOutcome.action === 'EAITT');
  const isNavigable =
    routeOutcome.kind === 'navigate' ||
    (routeOutcome.kind === 'special' &&
      routeOutcome.action === 'DSS' &&
      Boolean(storeNumber && onDssActivate)) ||
    (isTransfer && hasInventoryId(item) && Boolean(onTransferActivate));
  const activate = (trigger?: HTMLElement): void => {
    if (routeOutcome.kind === 'navigate') {
      push(routeOutcome.location);
    } else if (
      routeOutcome.kind === 'special' &&
      routeOutcome.action === 'DSS' &&
      storeNumber &&
      onDssActivate
    ) {
      onDssActivate(item, storeNumber);
    } else if (
      routeOutcome.kind === 'special' &&
      (routeOutcome.action === 'EAITF' || routeOutcome.action === 'EAITT') &&
      onTransferActivate
    ) {
      onTransferActivate(item, routeOutcome.action, trigger || document.body);
    }
  };

  return {
    activate,
    isNavigable,
    isUnavailable:
      routeOutcome.kind === 'unavailable' ||
      (isTransfer && !hasInventoryId(item)),
  };
};

const preventRowActivation = (event: React.SyntheticEvent): void => {
  event.stopPropagation();
};

type StoreActionConfirmDialogProps = {
  readonly action: 'complete' | 'reopen';
  readonly isOpen: boolean;
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
};

const StoreActionConfirmDialog: React.FC<StoreActionConfirmDialogProps> = ({
  action,
  isOpen,
  isPending,
  onCancel,
  onConfirm,
}) => {
  const titleKey =
    action === 'complete' ? 'confirmCompleteTitle' : 'confirmReopenTitle';
  const messageKey =
    action === 'complete' ? 'confirmCompleteMessage' : 'confirmReopenMessage';
  const actionKey = action === 'complete' ? 'markComplete' : 'markIncomplete';

  return (
    <Dialog
      aria-labelledby="store-action-confirmation-title"
      onClick={preventRowActivation}
      onClose={onCancel}
      open={isOpen}
    >
      <DialogTitle id="store-action-confirmation-title">
        {getStoreActionMessage(titleKey)}
      </DialogTitle>
      <DialogContent>{getStoreActionMessage(messageKey)}</DialogContent>
      <DialogActions>
        <Button disabled={isPending} onClick={onCancel} type="button">
          {getStoreActionMessage('cancel')}
        </Button>
        <Button
          color="primary"
          disabled={isPending}
          onClick={onConfirm}
          type="button"
        >
          {getStoreActionMessage(actionKey)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StoreActionStatusControl: React.FC<StoreActionStatusControlProps> = ({
  item,
  presentation,
  storeNumber,
}) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = React.useState(false);
  const { isPending, startStatusMutation } =
    useStoreActionStatusMutation(storeNumber);
  const isActive = item.status === 'ACTIVE';
  const actionKey = isActive ? 'markComplete' : 'markIncomplete';

  return (
    <>
      <button
        aria-label={getStoreActionMessage(actionKey)}
        className="store-action-row__action"
        disabled={isPending}
        onClick={(event) => {
          preventRowActivation(event);
          setIsConfirmationOpen(true);
        }}
        title={getStoreActionMessage(actionKey)}
        type="button"
      >
        {presentation === 'compact' ? (
          <FontAwesomeIcon
            aria-hidden="true"
            icon={faCheckCircle}
            style={{ fontSize: 25 }}
          />
        ) : (
          getStoreActionMessage(actionKey)
        )}
      </button>
      <StoreActionConfirmDialog
        action={isActive ? 'complete' : 'reopen'}
        isOpen={isConfirmationOpen}
        isPending={isPending}
        onCancel={() => setIsConfirmationOpen(false)}
        onConfirm={() => {
          if (startStatusMutation(item, isActive ? 'COMPLETED' : 'ACTIVE')) {
            setIsConfirmationOpen(false);
          }
        }}
      />
    </>
  );
};

type StoreActionRowViewProps = {
  readonly action: React.ReactNode;
  readonly className?: string;
  readonly date: React.ReactNode;
  readonly description: string;
  readonly displayType: string;
  readonly rowActivation: StoreActionRowActivation;
  readonly status: string;
  readonly colorToken: StoreActionThemeToken;
};

const StoreActionTableRow: React.FC<StoreActionRowViewProps> = ({
  action,
  className,
  colorToken,
  date,
  description,
  displayType,
  rowActivation,
  status,
}) => (
  <div
    aria-disabled={rowActivation.isUnavailable || undefined}
    aria-label={description}
    className={className}
    onClick={
      rowActivation.isNavigable
        ? (event) => rowActivation.activate(event.currentTarget)
        : undefined
    }
    role="row"
    tabIndex={rowActivation.isNavigable ? 0 : undefined}
    title={
      rowActivation.isUnavailable
        ? getStoreActionMessage('actionUnavailable')
        : undefined
    }
  >
    <Typography component="div" role="cell" variant="body2">
      {date}
    </Typography>
    <Typography
      className="store-action-row__chip"
      component="div"
      role="cell"
      style={{
        color: `var(${colorToken}, ${getStoreActionThemeFallbackColor(
          colorToken
        )})`,
      }}
      variant="body2"
    >
      {displayType}
    </Typography>
    <Typography
      aria-label={getStoreActionMessage('description')}
      component="div"
      role="cell"
      style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
      title={rowActivation.isUnavailable ? undefined : description || undefined}
      variant="body2"
    >
      {description}
    </Typography>
    <Typography component="div" role="cell" variant="body2">
      {status}
    </Typography>
    <div role="cell">{action}</div>
  </div>
);

const StoreActionCompactRow: React.FC<StoreActionRowViewProps> = ({
  action,
  className,
  colorToken,
  date,
  description,
  displayType,
  rowActivation,
}) => (
  <div
    className={className}
    role="listitem"
    title={
      rowActivation.isUnavailable
        ? getStoreActionMessage('actionUnavailable')
        : undefined
    }
  >
    <div
      aria-disabled={rowActivation.isUnavailable || undefined}
      aria-label={description}
      onClick={
        rowActivation.isNavigable
          ? (event) => rowActivation.activate(event.currentTarget)
          : undefined
      }
      role={
        rowActivation.isNavigable || rowActivation.isUnavailable
          ? 'button'
          : undefined
      }
      tabIndex={rowActivation.isNavigable ? 0 : undefined}
    >
      <div className="store-action-row__content">
        <div className="store-action-row__header">
          <Typography
            aria-label={getStoreActionMessage('description')}
            className="store-action-row__title"
            component="div"
            title={
              rowActivation.isUnavailable
                ? undefined
                : description || displayType
            }
            variant="body2"
          >
            {description || displayType}
          </Typography>
          <Typography
            className="store-action-row__chip"
            component="div"
            style={{
              color: `var(${colorToken}, ${getStoreActionThemeFallbackColor(
                colorToken
              )})`,
            }}
            variant="body2"
          >
            {displayType}
          </Typography>
        </div>
      </div>
    </div>
    <div className="store-action-row__footer">
      <Typography
        className="store-action-row__date"
        component="div"
        variant="body2"
      >
        {date}
      </Typography>
      {action}
    </div>
  </div>
);

export const StoreActionRow: React.FC<StoreActionRowProps> = ({
  className,
  item,
  onDssActivate,
  onTransferActivate,
  presentation = 'compact',
  role = '',
  storeNumber,
}) => {
  const history = useHistory();
  const push = (
    location: Parameters<ReturnType<typeof useHistory>['push']>[0]
  ): void => {
    history.push(location);
  };
  const featureFlags = React.useContext(
    FeatureFlagContext as React.Context<StoreActionFeatureFlags>
  );
  const resolvedCategory = resolveStoreActionCategory(item.storeActionCategory);
  const description = localizeStoreActionDescription(
    item.storeActionType,
    item.description
  );
  const tableDisplayType = getStoreActionMessage(
    CATEGORY_LABEL_KEYS[resolvedCategory.compactCategory]
  );
  const formattedDate = moment(item.createdAt.split('T')[0]).format(
    'MM/DD/YYYY'
  );
  const localizedDate = isDateTimeLocalizationEnabled(
    featureFlags.dateTimeLocalization
  );

  const date = localizedDate ? (
    <RACDateTimeLabel
      country={getCountryIdentification(featureFlags.countryIdentification)}
      displayType="ShortDate"
      value={formattedDate}
    />
  ) : (
    formattedDate
  );
  const status = getStoreActionMessage(
    item.status === 'ACTIVE' ? 'statusActive' : 'statusCompleted'
  );
  const rowActivation = getStoreActionRowActivation(
    item,
    push,
    storeNumber,
    onDssActivate,
    onTransferActivate
  );
  const action = item.isVirtual ? (
    <button
      aria-label={description}
      className="store-action-row__virtual-action"
      onClick={(event) => {
        preventRowActivation(event);
        push(PRINT_EDIT_PRICETAG_DESTINATION);
      }}
      title={
        presentation === 'compact' ? getStoreActionMessage('print') : undefined
      }
      type="button"
    >
      {presentation === 'compact' ? (
        <FontAwesomeIcon aria-hidden="true" icon={faPrint} />
      ) : (
        description
      )}
    </button>
  ) : storeNumber &&
    (item.status === 'ACTIVE' || canManageCompletedStoreActions(role)) ? (
    <StoreActionStatusControl
      item={item}
      presentation={presentation}
      storeNumber={storeNumber}
    />
  ) : null;

  const rowViewProps: StoreActionRowViewProps = {
    action,
    className,
    colorToken: resolvedCategory.colorToken,
    date,
    description,
    displayType: tableDisplayType,
    rowActivation,
    status,
  };

  return presentation === 'table' ? (
    <StoreActionTableRow {...rowViewProps} />
  ) : (
    <StoreActionCompactRow {...rowViewProps} />
  );
};
